// Types for dashboard data
type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'closed' | string;

// Backend data types
interface Activity {
  type: 'video_call' | 'token_transaction' | 'virtual_gift' | 'like' | 'referral';
  user: string;
  target_user?: string;
  time: string;
  icon: string;
  color: string;
  status?: string;
  location?: string;
  tokens?: number;
  transaction_type?: string;
  code?: string;
  message?: string;
}

interface ActiveUser {
  total: number;
  online: number;
  mobile: number;
  trend: string;
  peak: string;
  lastUpdated: string;
}

interface Match {
  users: [string, string];
  time: string;
  color: string;
  matchType?: string;
  location?: string;
}

interface Subscription {
  user: string;
  country: string;
  plan: 'Premium' | 'Black';
  time: string;
  icon: string;
  color: string;
  flag: string;
}

interface Location {
  city: string;
  country: string;
  count: number;
  trend: string;
  flag: string;
}

interface MetricUpdate {
  type: string;
  data: ActiveUser | Activity[] | Match[] | Subscription[] | Location[];
}

interface WebSocketMessage {
  type: 'connection_established' | 'metric_update' | 'error' | string;
  payload?: any;
  data?: any;
}

interface WebSocketError {
  message: string;
  details?: any;
}

// Message type mapping
const MESSAGE_TYPE_MAP = {
  'active_users': 'activeUsers',
  'recent_activities': 'activities',
  'recent_matches': 'matches',
  'recent_subscriptions': 'subscriptions',
  'top_locations': 'locations',
  'header_metrics': 'headerMetrics'
};

// Activity type to UI mapping
const ACTIVITY_UI_CONFIG = {
  video_call: {
    icon: '📹',
    color: '#2196F3',
    getMessage: (activity: Activity) => `Video call scheduled with ${activity.target_user}`,
  },
  token_transaction: {
    icon: '💎',
    color: '#FFD700',
    getMessage: (activity: Activity) => `${activity.tokens} tokens ${activity.transaction_type?.toLowerCase()}`,
  },
  virtual_gift: {
    icon: '🎁',
    color: '#E91E63',
    getMessage: (activity: Activity) => `Sent a gift to ${activity.target_user}`,
  },
  like: {
    icon: '❤️',
    color: '#F44336',
    getMessage: (activity: Activity) => `Liked ${activity.target_user}'s profile`,
  },
  referral: {
    icon: '👥',
    color: '#4CAF50',
    getMessage: (activity: Activity) => `Referred ${activity.target_user}`,
  }
};

// Custom EventEmitter for browser
class DashboardEventEmitter {
  private listeners: { [key: string]: Function[] } = {};

  on(event: string, callback: Function) {
    if (!this.listeners[event]) {
      this.listeners[event] = [];
    }
    this.listeners[event].push(callback);
  }

  off(event: string, callback: Function) {
    if (!this.listeners[event]) return;
    this.listeners[event] = this.listeners[event].filter(cb => cb !== callback);
  }

  emit(event: string, data?: any) {
    if (!this.listeners[event]) return;
    this.listeners[event].forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event listener for ${event}:`, error);
      }
    });
  }
}

// Create event emitter instance for dashboard events
export const dashboardEvents = new DashboardEventEmitter();

// List of metrics to subscribe to
const DASHBOARD_METRICS = [
  'active_users',
  'recent_activities',
  'recent_matches',
  'recent_subscriptions',
  'top_locations',
  'header_metrics'
];

// Configuration
const CONFIG = {
  reconnectAttempts: process.env.REACT_APP_ENV === 'production' ? 10 : 5,
  reconnectInterval: 2000,
  pingInterval: 30000, // 30 seconds
  connectionTimeout: 5000, // 5 seconds
};

// Add a global connection lock to prevent multiple connection attempts
let globalConnectionLock = false;
let lastConnectionAttempt = 0;
const CONNECTION_COOLDOWN = 10000; // 10 seconds cooldown between connection attempts

class DashboardWebSocket {
  private ws: WebSocket | null = null;
  private reconnectAttempts = 0;
  private maxReconnectAttempts = CONFIG.reconnectAttempts;
  private reconnectInterval = CONFIG.reconnectInterval;
  private reconnectTimeout: number | null = null;
  private pingTimeout: number | null = null;
  private connectionTimeout: number | null = null;
  private isConnecting = false;
  private usePollingFallback = false;
  private pollingInterval: number | null = null;
  private connected = false;

  constructor() {
    this.setupDebugListeners();
  }

  private setupDebugListeners() {
    if (process.env.REACT_APP_ENV !== 'production') {
      dashboardEvents.on('dashboard:connectionStatus', (status: ConnectionStatus) => {
        console.debug('[WebSocket] Connection status:', status);
      });

      dashboardEvents.on('dashboard:error', (error: WebSocketError) => {
        console.debug('[WebSocket] Error:', error);
      });
    }
  }

  public connect() {
    if (this.ws) {
      console.log('WebSocket already exists, disconnecting first');
      this.disconnect();
    }

    if (globalConnectionLock) {
      console.log('Global connection lock is active, skipping connection attempt');
      dashboardEvents.emit('websocket:error', 'Connection locked due to recent attempt');
      return;
    }

    globalConnectionLock = true;
    lastConnectionAttempt = Date.now();
    
    // Get WebSocket URL from environment
    const wsUrl = process.env.REACT_APP_WEBSOCKET_URL || '';
    if (!wsUrl) {
      console.error('WebSocket URL is not configured');
      dashboardEvents.emit('websocket:error', 'WebSocket URL is not configured');
      globalConnectionLock = false;
      return;
    }
    
    console.log('Connecting to WebSocket:', wsUrl);
    dashboardEvents.emit('websocket:connecting');
    
    try {
      this.ws = new WebSocket(wsUrl);
      
      this.ws.onopen = () => {
        console.log('WebSocket connection established');
        this.isConnecting = false;
        this.reconnectAttempts = 0;
        this.connected = true;
        
        // Clear connection timeout
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        
        dashboardEvents.emit('websocket:connected');
        
        // Set up ping to keep connection alive
        this.setupPing();
      };

      this.ws.onmessage = (event) => {
        try {
          console.log('WebSocket message received:', event.data.substring(0, 100) + (event.data.length > 100 ? '...' : ''));
          const message = JSON.parse(event.data);
          this.handleMessage(message);
        } catch (error) {
          console.error('Error parsing WebSocket message:', error);
          console.error('Raw message data:', event.data);
          dashboardEvents.emit('websocket:error', {
            message: 'Error parsing WebSocket message',
            details: { error, data: event.data.substring(0, 200) }
          });
        }
      };

      this.ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        this.isConnecting = false;
        this.connected = false;
        dashboardEvents.emit('websocket:error', error);
        
        // Don't automatically reconnect on error to prevent cycling
        // Release the global connection lock
        globalConnectionLock = false;
        
        dashboardEvents.emit('websocket:disconnected');
      };

      this.ws.onclose = (event) => {
        console.log('WebSocket connection closed:', event);
        this.isConnecting = false;
        this.connected = false;
        this.reconnectAttempts = 0;
        
        // Clear connection timeout if still connecting
        if (this.connectionTimeout) {
          clearTimeout(this.connectionTimeout);
          this.connectionTimeout = null;
        }
        
        // Update state
        this.isConnecting = false;
        
        // Emit event
        dashboardEvents.emit('websocket:disconnected');
        
        // Handle reconnection if needed
        if (event.code !== 1000) {
          this.handleDisconnect();
        } else {
          // Clean close, release the global connection lock
          globalConnectionLock = false;
        }
      };
    } catch (error) {
      console.error('Error creating WebSocket:', error);
      globalConnectionLock = false;
      dashboardEvents.emit('websocket:error', error);
      
      // Clean up any partial connection
      this.cleanup();
      if (this.ws) {
        try {
          this.ws.close();
        } catch (e) {
          console.error('Error closing WebSocket after connection error:', e);
        }
        this.ws = null;
      }
    }
  }

  private setupPing() {
    // Clear any existing ping timeout
    if (this.pingTimeout) {
      window.clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }

    // Set up a new ping timeout
    this.pingTimeout = window.setTimeout(() => {
      if (this.ws?.readyState === WebSocket.OPEN) {
        // Send a ping message
        try {
          console.log('Sending ping to keep connection alive...');
          this.send({ type: 'ping', timestamp: Date.now() });
          
          // Set up the next ping
          this.setupPing();
        } catch (error) {
          console.error('Error sending ping:', error);
          // If we can't send a ping, the connection might be dead
          this.handleDisconnect();
        }
      } else {
        console.warn('WebSocket not open, cannot send ping');
        // If the connection is not open, try to reconnect
        this.handleDisconnect();
      }
    }, 30000); // Send a ping every 30 seconds
  }

  private cleanup() {
    console.log('Cleaning up WebSocket resources...');
    
    // Clear ping timeout
    if (this.pingTimeout) {
      console.log('Clearing ping timeout');
      window.clearTimeout(this.pingTimeout);
      this.pingTimeout = null;
    }
    
    // Clear connection timeout
    if (this.connectionTimeout) {
      console.log('Clearing connection timeout');
      window.clearTimeout(this.connectionTimeout);
      this.connectionTimeout = null;
    }
    
    // Clear reconnect timeout
    if (this.reconnectTimeout) {
      console.log('Clearing reconnect timeout');
      window.clearTimeout(this.reconnectTimeout);
      this.reconnectTimeout = null;
    }
    
    // Clear polling interval
    if (this.pollingInterval) {
      console.log('Clearing polling interval');
      window.clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
  }

  private formatActivityData(activity: any): any {
    if (!activity) return null;
    
    try {
      // Ensure we have the required fields
      if (!activity.type || !activity.user) {
        console.warn('Invalid activity data:', activity);
        return null;
      }
      
      // Set default values for missing fields
      const formattedActivity = {
        ...activity,
        time: activity.time || new Date().toISOString(),
        icon: activity.icon || this.getDefaultIcon(activity.type),
        color: activity.color || this.getDefaultColor(activity.type)
      };
      
      return formattedActivity;
    } catch (error) {
      console.error('Error formatting activity data:', error);
      return null;
    }
  }
  
  private getDefaultIcon(type: string): string {
    switch (type) {
      case 'video_call': return 'ri-video-chat-line';
      case 'token_transaction': return 'ri-coin-line';
      case 'virtual_gift': return 'ri-gift-line';
      case 'like': return 'ri-heart-line';
      case 'referral': return 'ri-user-add-line';
      default: return 'ri-notification-line';
    }
  }
  
  private getDefaultColor(type: string): string {
    switch (type) {
      case 'video_call': return '#4CAF50';
      case 'token_transaction': return '#FFC107';
      case 'virtual_gift': return '#9C27B0';
      case 'like': return '#F44336';
      case 'referral': return '#2196F3';
      default: return '#607D8B';
    }
  }

  private handleMessage(message: WebSocketMessage) {
    try {
      console.log('Handling WebSocket message:', message);
      
      // Handle different message types
      switch (message.type) {
        case 'connection_established':
          console.log('Connection established with server');
          dashboardEvents.emit('websocket:connected');
          break;
          
        case 'authentication_success':
        case 'authenticated':
          console.log('Authentication successful');
          dashboardEvents.emit('websocket:connected');
          break;
          
        case 'authentication_failed':
          console.error('Authentication failed:', message.payload || message.data);
          dashboardEvents.emit('websocket:error', {
            message: 'WebSocket authentication failed',
            details: message.payload || message.data
          });
          break;
          
        case 'subscription_success':
        case 'subscribed':
          console.log('Subscription successful');
          dashboardEvents.emit('websocket:connected');
          break;
          
        case 'subscription_failed':
          console.error('Subscription failed:', message.payload || message.data);
          dashboardEvents.emit('websocket:error', {
            message: 'WebSocket subscription failed',
            details: message.payload || message.data
          });
          break;
          
        case 'pong':
          console.log('Received pong from server');
          // Server responded to our ping, connection is healthy
          break;
          
        case 'error':
          console.error('Server error:', message.payload || message.data);
          dashboardEvents.emit('websocket:error', message.payload || message.data);
          break;
          
        case 'metric_update':
          this.handleMetricUpdate(message.payload || message.data);
          break;
          
        default:
          console.log('Unhandled message type:', message.type);
          
          // Check if this is an authentication response
          if (message.type && message.type.includes('auth')) {
            console.log('Possible authentication response:', message);
            dashboardEvents.emit('websocket:connected');
          }
          
          // Check if this is a subscription response
          if (message.type && message.type.includes('subscri')) {
            console.log('Possible subscription response:', message);
            dashboardEvents.emit('websocket:connected');
          }
          
          // Try to handle the message as a metric update if it has the right structure
          if (message.data && typeof message.data === 'object') {
            this.handleMetricUpdate(message.data);
          }
      }
    } catch (error) {
      console.error('Error handling message:', error);
      dashboardEvents.emit('websocket:error', {
        message: 'Error processing server message',
        details: { error, messageType: message?.type }
      });
    }
  }

  private handleDisconnect(immediate = false) {
    this.cleanup();
    
    // Make sure the WebSocket is properly closed
    if (this.ws) {
      try {
        if (this.ws.readyState !== WebSocket.CLOSED) {
          this.ws.close();
        }
      } catch (e) {
        console.error('Error closing WebSocket during disconnect:', e);
      }
      this.ws = null;
    }
    
    // Check global connection lock
    if (globalConnectionLock) {
      console.log('Global connection lock active, skipping reconnection');
      return;
    }
    
    // Check connection cooldown
    const now = Date.now();
    if (now - lastConnectionAttempt < CONNECTION_COOLDOWN) {
      console.log(`Connection cooldown active (${Math.round((CONNECTION_COOLDOWN - (now - lastConnectionAttempt)) / 1000)}s remaining), skipping reconnection`);
      return;
    }
    
    // After several reconnect attempts, switch to polling fallback
    if (this.reconnectAttempts >= 3 && !this.usePollingFallback) {
      console.warn(`Multiple reconnect attempts failed. Switching to polling fallback.`);
      this.setupPollingFallback();
      return;
    }
    
    // If we've reached max attempts and polling fallback is not an option
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.warn(`Maximum reconnect attempts (${this.maxReconnectAttempts}) reached. Giving up.`);
      dashboardEvents.emit('websocket:disconnected');
      dashboardEvents.emit('websocket:error', { 
        message: `Failed to reconnect after ${this.maxReconnectAttempts} attempts. Please refresh the page.`
      });
      return;
    }
    
    this.reconnectAttempts++;
    
    // Set global connection lock and update last attempt time
    globalConnectionLock = true;
    lastConnectionAttempt = now;
    
    if (immediate) {
      console.log('Attempting immediate reconnection...');
      dashboardEvents.emit('websocket:reconnecting immediately');
      
      // Small delay even for immediate reconnection to allow resources to clean up
      this.reconnectTimeout = window.setTimeout(() => {
        this.connect();
      }, 5000); // 5 second delay even for "immediate" reconnection
      return;
    }
    
    // Use exponential backoff with a maximum delay cap
    const baseDelay = this.reconnectInterval;
    const exponentialDelay = baseDelay * Math.pow(2, this.reconnectAttempts - 1);
    const delay = Math.min(exponentialDelay, 60000); // Cap at 60 seconds
    
    console.log(`Attempting to reconnect (${this.reconnectAttempts}/${this.maxReconnectAttempts}) in ${delay}ms...`);
    
    dashboardEvents.emit('websocket:reconnecting');
    
    this.reconnectTimeout = window.setTimeout(() => {
      this.connect();
    }, delay);
  }

  public disconnect() {
    console.log('Disconnecting WebSocket');
    
    // Clear all timeouts and intervals
    this.cleanup();
    
    // Close WebSocket connection if it exists
    if (this.ws) {
      try {
        // Check if the connection is already closed or closing
        if (this.ws.readyState === WebSocket.CONNECTING || 
            this.ws.readyState === WebSocket.OPEN) {
          this.ws.close(1000, 'Closed by client');
        }
      } catch (error) {
        console.error('Error closing WebSocket:', error);
      }
      
      // Reset WebSocket instance
      this.ws = null;
    }
    
    // Reset state
    this.isConnecting = false;
    this.reconnectAttempts = 0;
    
    // Release global connection lock
    globalConnectionLock = false;
    
    // Emit event
    dashboardEvents.emit('websocket:disconnected');
    
    this.connected = false;
  }

  // Add method to send messages
  public send(message: any) {
    if (!this.ws) {
      console.error('Cannot send message: WebSocket not initialized');
      dashboardEvents.emit('websocket:error', { 
        message: 'Cannot send message: WebSocket not initialized'
      });
      return;
    }
    
    if (this.ws.readyState !== WebSocket.OPEN) {
      console.error(`Cannot send message: WebSocket not open (readyState: ${this.ws.readyState})`);
      dashboardEvents.emit('websocket:error', { 
        message: `Cannot send message: WebSocket not open (readyState: ${this.ws.readyState})`
      });
      return;
    }
    
    try {
      // Convert message to string if it's an object
      const messageString = typeof message === 'object' ? JSON.stringify(message) : message;
      
      console.log('Sending WebSocket message:', message);
      this.ws.send(messageString);
    } catch (error) {
      console.error('Error sending WebSocket message:', error);
      console.error('Message that failed to send:', message);
      dashboardEvents.emit('websocket:error', { 
        message: 'Error sending WebSocket message',
        details: { error, messageType: message?.type }
      });
    }
  }

  // Getter for WebSocket instance
  public get webSocket(): WebSocket | null {
    return this.ws;
  }

  private handleMetricUpdate(data: any) {
    if (!data || typeof data !== 'object') {
      console.warn('Invalid metric update data:', data);
      return;
    }

    try {
      console.log('Processing metric update:', data);
      
      // Helper function to remove avatar URLs from objects
      const removeAvatarUrls = (item: any) => {
        if (!item || typeof item !== 'object') return item;
        
        const processed = { ...item };
        
        // Common avatar URL fields
        const avatarFields = [
          'avatar_url', 'profile_pic', 'avatar', 'image', 'img', 
          'user_avatar', 'target_avatar_url', 'user1_avatar', 'user2_avatar'
        ];
        
        // Remove all avatar fields
        avatarFields.forEach(field => {
          if (field in processed) {
            delete processed[field];
          }
        });
        
        return processed;
      };
      
      // Process arrays to remove avatar URLs
      const processArray = (arr: any[]) => {
        if (!Array.isArray(arr)) return arr;
        return arr.map(item => removeAvatarUrls(item));
      };
      
      // Handle different types of metric updates
      if (data.type === 'active_users' && data.data) {
        const processedData = processArray(data.data);
        console.log(`Processing active_users: ${processedData.length} items`);
        dashboardEvents.emit('dashboard:activeUsers', processedData);
      } 
      else if (data.type === 'recent_activities' && data.data) {
        const activities = Array.isArray(data.data) ? processArray(data.data) : [];
        console.log(`Processing recent_activities: ${activities.length} items`);
        dashboardEvents.emit('dashboard:activities', activities);
      } 
      else if (data.type === 'recent_matches' && data.data) {
        const processedData = processArray(data.data);
        console.log(`Processing recent_matches: ${processedData.length} items`);
        dashboardEvents.emit('dashboard:matches', processedData);
      } 
      else if (data.type === 'recent_subscriptions' && data.data) {
        const processedData = processArray(data.data);
        console.log(`Processing recent_subscriptions: ${processedData.length} items`);
        dashboardEvents.emit('dashboard:subscriptions', processedData);
      } 
      else if (data.type === 'top_locations' && data.data) {
        const processedData = processArray(data.data);
        console.log(`Processing top_locations: ${processedData.length} items`);
        dashboardEvents.emit('dashboard:locations', processedData);
      } 
      else if (data.type === 'header_metrics' && data.data) {
        dashboardEvents.emit('dashboard:headerMetrics', data.data);
      }
      else if (data.type === 'user_growth' && data.data) {
        console.log(`Processing user_growth data`);
        dashboardEvents.emit('dashboard:userGrowth', data.data);
      }
      else if (data.type === 'match_analytics' && data.data) {
        console.log(`Processing match_analytics data`);
        dashboardEvents.emit('dashboard:matchAnalytics', data.data);
      }
      else if (data.type === 'revenue_analytics' && data.data) {
        console.log(`Processing revenue_analytics data`);
        dashboardEvents.emit('dashboard:revenueAnalytics', data.data);
      }
      else if (data.type === 'user_demographics' && data.data) {
        console.log(`Processing user_demographics data`);
        dashboardEvents.emit('dashboard:userDemographics', data.data);
      }
      else if (data.type === 'age_distribution' && data.data) {
        console.log(`Processing age_distribution data`);
        dashboardEvents.emit('dashboard:ageDistribution', data.data);
      }
      else if (data.type === 'gender_distribution' && data.data) {
        console.log(`Processing gender_distribution data`);
        dashboardEvents.emit('dashboard:genderDistribution', data.data);
      }
      else if (data.type === 'subscription_distribution' && data.data) {
        console.log('Emitting subscription distribution data:', data.data);
        dashboardEvents.emit('dashboard:subscriptionDistribution', data.data);
      }
      else if (data.type === 'geographic_distribution' && data.data) {
        console.log('Emitting geographic distribution data:', data.data);
        dashboardEvents.emit('dashboard:geographicDistribution', data.data);
      }
      else {
        // If we have a direct data structure without a type field
        if (data.active_users) {
          const processedData = processArray(data.active_users);
          dashboardEvents.emit('dashboard:activeUsers', processedData);
        }
        if (data.activities) {
          const processedData = processArray(data.activities);
          dashboardEvents.emit('dashboard:activities', processedData);
        }
        if (data.matches) {
          const processedData = processArray(data.matches);
          dashboardEvents.emit('dashboard:matches', processedData);
        }
        if (data.subscriptions) {
          const processedData = processArray(data.subscriptions);
          dashboardEvents.emit('dashboard:subscriptions', processedData);
        }
        if (data.locations) {
          const processedData = processArray(data.locations);
          dashboardEvents.emit('dashboard:locations', processedData);
        }
        if (data.header_metrics) {
          dashboardEvents.emit('dashboard:headerMetrics', data.header_metrics);
        }
        if (data.user_growth) {
          dashboardEvents.emit('dashboard:userGrowth', data.user_growth);
        }
        if (data.match_analytics) {
          dashboardEvents.emit('dashboard:matchAnalytics', data.match_analytics);
        }
        if (data.revenue_analytics) {
          dashboardEvents.emit('dashboard:revenueAnalytics', data.revenue_analytics);
        }
        if (data.user_demographics) {
          dashboardEvents.emit('dashboard:userDemographics', data.user_demographics);
        }
        if (data.age_distribution) {
          dashboardEvents.emit('dashboard:ageDistribution', data.age_distribution);
        }
        if (data.gender_distribution) {
          dashboardEvents.emit('dashboard:genderDistribution', data.gender_distribution);
        }
        if (data.subscription_distribution) {
          console.log('Emitting subscription distribution data:', data.subscription_distribution);
          dashboardEvents.emit('dashboard:subscriptionDistribution', data.subscription_distribution);
        }
        if (data.geographic_distribution) {
          console.log('Emitting geographic distribution data:', data.geographic_distribution);
          dashboardEvents.emit('dashboard:geographicDistribution', data.geographic_distribution);
        }
      }
    } catch (error) {
      console.error('Error processing metric update:', error);
      dashboardEvents.emit('websocket:error', {
        message: 'Error processing metric update',
        details: { error, data }
      });
    }
  }

  // Method to set up polling as a fallback
  private setupPollingFallback() {
    console.log('Setting up polling fallback mechanism');
    
    // Clear any existing polling interval
    if (this.pollingInterval) {
      window.clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    // Mark that we're using the fallback
    this.usePollingFallback = true;
    dashboardEvents.emit('websocket:using polling fallback');
    
    // Set up polling interval
    this.pollingInterval = window.setInterval(() => {
      this.pollDashboardData();
    }, 10000); // Poll every 10 seconds
    
    // Do an initial poll immediately
    this.pollDashboardData();
  }
  
  // Method to poll dashboard data using HTTP
  private async pollDashboardData() {
    try {
      console.log('Polling dashboard data...');
      
      const apiUrl = process.env.REACT_APP_API_URL1 || '';
      const authToken = JSON.parse(sessionStorage.getItem('authUser') || '{}')?.token;
      
      if (!apiUrl || !authToken) {
        console.error('API URL or auth token missing for polling');
        return;
      }
      
      // Make HTTP request to get dashboard data
      const response = await fetch(`${apiUrl}analytics/dashboard/summary`, {
        method: 'GET',
        headers: {
          'Authorization': `${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('Received polling data:', data);
      
      // Process the data as if it came from WebSocket
      if (data.success && data.data) {
        this.handleMetricUpdate(data.data);
      }
    } catch (error) {
      console.error('Error polling dashboard data:', error);
    }
  }

  // Add isConnected method
  public isConnected(): boolean {
    return this.connected && this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}

// Create and export singleton instance
export const dashboardWebSocket = new DashboardWebSocket();